import React, { useEffect, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { Button, Input } from '../../ui';

interface GetCrmEntityConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

type TargetEntity = 'contact' | 'company' | 'deal' | 'lead';

const ENTITY_LABEL: Record<TargetEntity, string> = {
  contact: 'Contato',
  company: 'Empresa',
  deal: 'Negócio',
  lead: 'Lead',
};

export const GetCrmEntityConfig: React.FC<GetCrmEntityConfigProps> = ({ config, onChange }) => {
  const { bitrixFields } = useFlowBuilderStore();
  const targetEntity: TargetEntity =
    config.targetEntity === 'contact' || config.targetEntity === 'company' || config.targetEntity === 'lead'
      ? config.targetEntity
      : 'deal';

  const fetchFieldIds = Array.isArray(config.fetchFieldIds) ? config.fetchFieldIds : [];
  const fetchFieldValueMode = String(config.fetchFieldValueMode || 'both').trim().toLowerCase() === 'code'
    ? 'code'
    : String(config.fetchFieldValueMode || 'both').trim().toLowerCase() === 'label'
      ? 'label'
      : 'both';
  const responseVariable = String(config.responseVariable || 'fetched_entity')
    .trim()
    .replace(/\s+/g, '_');

  useEffect(() => {
    if (config.mode !== 'get_crm_entity') {
      onChange('mode', 'get_crm_entity');
    }
  }, [config.mode, onChange]);

  const entityFields = useMemo(() => {
    const fields = (bitrixFields?.[targetEntity] || {}) as Record<string, any>;
    return Object.entries(fields)
      .map(([fieldId, field]) => ({
        id: String(fieldId || '').trim(),
        label: String(field?.formLabel || field?.title || fieldId).trim(),
      }))
      .filter((field) => field.id)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bitrixFields, targetEntity]);

  const updateFieldAt = (index: number, fieldId: string) => {
    const next = [...fetchFieldIds];
    next[index] = fieldId;
    onChange('fetchFieldIds', next);
  };

  const addField = () => {
    onChange('fetchFieldIds', [...fetchFieldIds, '']);
  };

  const removeField = (index: number) => {
    const next = [...fetchFieldIds];
    next.splice(index, 1);
    onChange('fetchFieldIds', next);
  };

  const previewField = fetchFieldIds.find((fieldId: string) => String(fieldId || '').trim());
  const previewVariable = previewField || '';

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-200 leading-relaxed">
        <strong className="text-blue-800 dark:text-blue-100">Obter CRM:</strong> busca um contato,
        empresa ou negócio por ID no Bitrix24 e salva o resultado em variáveis do fluxo.
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Entidade para obter
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={targetEntity}
          onChange={(e) => onChange('targetEntity', e.target.value)}
        >
          <option value="contact">Contato</option>
          <option value="company">Empresa</option>
          <option value="deal">Negócio</option>
          <option value="lead">Lead</option>
        </select>
      </div>

      <Input
        label={`ID do ${ENTITY_LABEL[targetEntity]} para buscar`}
        placeholder="Ex: {{found_entity_id}}"
        value={config.entityId ?? '{{found_entity_id}}'}
        onChange={(e) => onChange('entityId', e.target.value)}
        helperText="Obrigatório."
      />

      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3 dark:bg-slate-900/20">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100 uppercase tracking-wide">
            Campos para retornar (opcional)
          </h4>
          <Button variant="outline" size="sm" onClick={addField}>
            <Plus className="w-3 h-3 mr-1" />
            Campo
          </Button>
        </div>

        {fetchFieldIds.length === 0 ? (
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Se nenhum campo for selecionado, o nó retorna o objeto completo da entidade.
          </p>
        ) : (
          <div className="space-y-2">
            {fetchFieldIds.map((fieldId: string, index: number) => (
              <div key={`${fieldId || 'empty'}-${index}`} className="grid grid-cols-12 gap-2 items-center">
                <select
                  className="col-span-11 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
                  value={fieldId || ''}
                  onChange={(e) => updateFieldAt(index, e.target.value)}
                >
                  <option value="">Selecione um campo</option>
                  {entityFields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label} ({field.id})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="col-span-1 inline-flex items-center justify-center p-1.5 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                  onClick={() => removeField(index)}
                  title="Remover campo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
          Formato para campos de lista
        </label>
        <select
          className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md"
          value={fetchFieldValueMode}
          onChange={(e) => onChange('fetchFieldValueMode', e.target.value)}
        >
          <option value="both">Nome + código</option>
          <option value="label">Somente nome</option>
          <option value="code">Somente código</option>
        </select>
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">
          Para campos como <strong>TYPE_ID</strong>, você pode retornar o nome exibido, o código interno ou ambos.
        </p>
      </div>

      <Input
        label="Variável de saída (objeto)"
        placeholder="fetched_entity"
        value={responseVariable}
        onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s+/g, '_'))}
        helperText="Exemplo de uso: {{fetched_entity.TITLE}}"
      />

      <p className="text-xs text-slate-600 dark:text-slate-400">
        Campos selecionados viram variáveis diretas pelo <strong>FIELD_ID</strong>.
        {' '}
        {fetchFieldValueMode === 'both'
          ? (
            previewVariable
              ? `Exemplo: {{${previewVariable}}} (nome), {{${previewVariable}_code}} (código).`
              : 'Exemplo: {{TYPE_ID}} (nome), {{TYPE_ID_code}} (código).'
          )
          : (
            previewVariable
              ? `Exemplo: {{${previewVariable}}}.`
              : '{{TYPE_ID}}.'
          )}
      </p>

      <p className="text-[11px] text-slate-500 dark:text-slate-300">
        ID retornado automaticamente em <strong>{'{{fetched_entity_id}}'}</strong>.
        {' '}
        No seletor de variáveis deste nó, mostramos a variável de saída, o ID automático e os campos escolhidos.
      </p>
    </div>
  );
};
